import { useMemo } from 'react'

import { sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore } from '@domain-group/react-hooks'

import matchFilterString from '@functions/utils/matchFilterString'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'
import useWatchEntityUpdates from '@hooks/useWatchEntityUpdates'

import type { BrandRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['brands']

type BrandFilters = {
  isArchived?: boolean,
  string?: string,
}

type UseBrandsOptions = {
  filters?: BrandFilters,
  performHttpRequests?: boolean,
  requestOptions?: BrandRequestOptions,
}

function useBrands(options: UseBrandsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    isArchived: filterIsArchived,
    string: filterString,
  } = filters

  const {
    updatedEntities: { brands: brandsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { brands } = useSelector(reduxState => reduxState.entities)

  const filteredBrands = useMemo(() => {
    const filtered = Object.values(brands).filter((brand) => {
      const { id, isArchived, name } = brand

      const matchIsArchived = filterIsArchived !== undefined ? isArchived === filterIsArchived : true

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(name, filterString)
      const matchString = matchId || matchTitle

      return matchIsArchived && matchString
    })

    return sortArrayBy(filtered, 'asc', 'name')
  }, [brandsUpdatedAt, JSON.stringify(filters)])

  const filteredBrandsCount = filteredBrands.length
  const hasFilteredBrands = !!filteredBrandsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrands } = useReduxAction(
    'brands',
    'loadBrands',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrands,
    filteredBrandsCount,
    hasFilteredBrands,
    loading: loadingBrands,
  }
}

export default useBrands
